import type { ElementType as ReactElementType, HTMLAttributes, Ref } from 'react'
import type { VariantProps } from 'tailwind-variants'

import { cn } from '@gto/ui/cn'

import { typographyVariants } from './typography.styles'

type TypographyVariantProps = VariantProps<typeof typographyVariants>

export type TypographyVariant = TypographyVariantProps['variant']
export type TypographyColor = TypographyVariantProps['color']

type ElementType = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'p' | 'span'

const variantElementMap: Record<string, ElementType> = {
  h1: 'h1',
  h2: 'h2',
  h3: 'h3',
  h4: 'h4',
  h5: 'h5',
  h6: 'h6',
  body1: 'p',
  body2: 'p',
  body3: 'p',
  'body3/cap': 'p',
  body4: 'p',
  'body4/cap': 'p',
  subtitle1: 'h6',
  subtitle2: 'h6',
  subtitle3: 'h6',
  overline: 'span',
  caption: 'span',
  badge: 'span',
  'badge/label': 'span',
  'input/label': 'span',
  'input/value/small': 'span',
  'input/value/medium': 'span',
  'input/helper': 'span',
  'tooltip/label': 'span',
}

export type TypographyProps = HTMLAttributes<HTMLElement> &
  TypographyVariantProps & {
    component?: ReactElementType
    ref?: Ref<HTMLElement>
  }

export const Typography = ({
  variant = 'body1',
  color,
  component,
  className,
  children,
  ref,
  gutterBottom,
  noWrap,
  ...restProps
}: TypographyProps) => {
  const Tag = component ?? variantElementMap[variant] ?? 'p'

  return (
    <Tag
      ref={ref}
      className={cn(typographyVariants({ variant, color, gutterBottom, noWrap }), className)}
      {...restProps}
    >
      {children}
    </Tag>
  )
}
